import { getErrorMessage } from '@repo/lib'
import { Pattern } from '@repo/types'
import { Heading, merge, MiniBingoCard, TargetDisplay } from '@repo/ui'
import * as Sentry from '@sentry/react'
import { observer } from 'mobx-react-lite'
import React from 'react'

import { apiClient } from '@/api/api-client'
import { Select } from '@/components/shared/Select'
import { useDialogContext } from '@/dialogs/DialogContext'
import { useGameContext } from '@/pages/game/context/game-context'

interface BingoTargetSettingsProps {
  className?: string
  disableConfirmPrompt?: boolean
  disableMessageQueue?: boolean
}

export const BingoTargetSettings = observer(function BingoTargetSettings({
  className,
  disableConfirmPrompt,
  disableMessageQueue
}: BingoTargetSettingsProps): React.ReactElement {
  return (
    <div className={merge('border-shade-3 border-b p-4 last:border-b-0 md:p-6', className)}>
      <Heading className='mb-2'>Bingo Target</Heading>
      <BingoTargetSelector
        disableConfirmPrompt={disableConfirmPrompt}
        disableMessageQueue={disableMessageQueue}
      />
    </div>
  )
})

interface BingoTargetSelectorProps {
  disableConfirmPrompt?: boolean
  disableMessageQueue?: boolean
}

const BingoTargetSelector = observer(function BingoTargetSelector({
  disableConfirmPrompt = false,
  disableMessageQueue = false
}: BingoTargetSelectorProps): React.ReactElement {
  const { setConfirmPrompt, setErrorDialog } = useDialogContext()

  const { gameData, gameId, tracker } = useGameContext()

  const { currentTarget } = gameData

  const updateTarget = async (target: Pattern) => {
    try {
      await apiClient.updateTarget(gameId, target, disableMessageQueue)
      tracker.changeTarget(target)
    } catch (error) {
      setErrorDialog({ error: getErrorMessage(error) })
      Sentry.captureException(error)
    }
  }

  const handleChange = async (target: Pattern) => {
    if (disableConfirmPrompt) {
      await updateTarget(target)
    } else {
      setConfirmPrompt({
        description: 'Guests will receive an update on their devices.',
        heading: 'Are you sure you want to change the pattern that guests are looking for?',
        onConfirm: async () => await updateTarget(target)
      })
    }
  }

  return (
    <>
      <Select
        options={[
          {
            description: 'At least one line in any direction',
            graphic: <MiniBingoCard pattern={Pattern.ONE_LINE} size='small' />,
            id: Pattern.ONE_LINE,
            name: 'One Line',
            value: Pattern.ONE_LINE
          },
          {
            description: 'At least two lines in any direction',
            graphic: <MiniBingoCard pattern={Pattern.TWO_LINES} size='small' />,
            id: Pattern.TWO_LINES,
            name: 'Two Lines',
            value: Pattern.TWO_LINES
          },
          {
            description: 'At least three lines in any direction',
            graphic: <MiniBingoCard pattern={Pattern.THREE_LINES} size='small' />,
            id: Pattern.THREE_LINES,
            name: 'Three Lines',
            value: Pattern.THREE_LINES
          },
          {
            description: 'At least four lines in any direction',
            graphic: <MiniBingoCard pattern={Pattern.FOUR_LINES} size='small' />,
            id: Pattern.FOUR_LINES,
            name: 'Four Lines',
            value: Pattern.FOUR_LINES
          },
          {
            description: 'Each corner square on the card',
            graphic: <MiniBingoCard pattern={Pattern.CORNERS} size='small' />,
            id: Pattern.CORNERS,
            name: 'Four Corners',
            value: Pattern.CORNERS
          },
          {
            description: 'Four lines around the outer border of the card',
            graphic: <MiniBingoCard pattern={Pattern.EDGES} size='small' />,
            id: Pattern.EDGES,
            name: 'Edges',
            value: Pattern.EDGES
          },
          {
            description: 'Both diagonal lines on the card',
            graphic: <MiniBingoCard pattern={Pattern.X_SHAPE} size='small' />,
            id: Pattern.X_SHAPE,
            name: 'X Shape',
            value: Pattern.X_SHAPE
          },
          {
            description: 'Every square on the card',
            graphic: <MiniBingoCard pattern={Pattern.FULL_CARD} size='small' />,
            id: Pattern.FULL_CARD,
            name: 'Full Card',
            value: Pattern.FULL_CARD
          }
        ]}
        value={currentTarget}
        onChange={(target: Pattern) => void handleChange(target)}
      />
      <div className='mt-6 flex items-center justify-center text-center'>
        <TargetDisplay pattern={currentTarget} size='medium' />
      </div>
    </>
  )
})
